import React from 'react';

const FormattedMessage = ({ content }) => {
  // Function to format text with basic markdown-like styling
  const formatText = (text) => {
    // Split by double line breaks to create paragraphs
    const paragraphs = text.split('\n\n');
    
    return paragraphs.map((paragraph, pIndex) => {
      if (!paragraph.trim()) return null;
      
      // Check if it's a header (starts with ##)
      if (paragraph.startsWith('##')) {
        return (
          <h3 key={pIndex} className="text-lg font-semibold text-gray-800 mt-4 mb-2">
            {paragraph.replace('##', '').trim()}
          </h3>
        );
      }
      
      // Check if it's a subheader (starts with **text**)
      if (paragraph.match(/^\*\*.*\*\*$/)) {
        return (
          <h4 key={pIndex} className="text-md font-semibold text-gray-700 mt-3 mb-2">
            {paragraph.replace(/\*\*(.*)\*\*/, '$1')}
          </h4>
        );
      }
      
      // Process the paragraph for inline formatting
      let formattedParagraph = paragraph;
      
      // Split by single line breaks to handle lists and lines
      const lines = formattedParagraph.split('\n');
      
      return (
        <div key={pIndex} className="mb-4">
          {lines.map((line, lIndex) => {
            if (!line.trim()) return null;
            
            // Handle bullet points (• or -)
            if (line.trim().match(/^[•\-\*]\s/)) {
              return (
                <div key={lIndex} className="flex items-start mb-1 ml-4">
                  <span className="text-blue-500 mr-2 mt-1">•</span>
                  <span className="flex-1">{formatInlineElements(line.replace(/^[•\-\*]\s/, ''))}</span>
                </div>
              );
            }
            
            // Handle numbered lists
            if (line.trim().match(/^\d+\.\s/)) {
              const number = line.match(/^(\d+)\./)[1];
              return (
                <div key={lIndex} className="flex items-start mb-1 ml-4">
                  <span className="text-blue-500 mr-2 mt-1 font-medium">{number}.</span>
                  <span className="flex-1">{formatInlineElements(line.replace(/^\d+\.\s/, ''))}</span>
                </div>
              );
            }
            
            // Handle section headers that start with emojis
            if (line.trim().match(/^[🔧📚💬🤖📖✨💭🧠💻🌤️⏰😄🙏🔍]/)) {
              return (
                <div key={lIndex} className="font-semibold text-gray-800 mt-3 mb-2 text-base">
                  {formatInlineElements(line)}
                </div>
              );
            }
            
            // Handle horizontal rules (---)
            if (line.trim() === '---') {
              return <hr key={lIndex} className="my-4 border-gray-300" />;
            }
            
            // Regular paragraph line
            return (
              <div key={lIndex} className="mb-2 leading-relaxed">
                {formatInlineElements(line)}
              </div>
            );
          })}
        </div>
      );
    });
  };
  
  // Function to handle inline formatting elements
  const formatInlineElements = (text) => {
    const parts = [];
    let currentText = text;
    let key = 0;
    
    // Process **bold** text
    const boldRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(
          <span key={key++}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add the bold text
      parts.push(
        <strong key={key++} className="font-semibold text-gray-800">
          {match[1]}
        </strong>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={key++}>
          {text.substring(lastIndex)}
        </span>
      );
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  return (
    <div className="formatted-message">
      {formatText(content)}
    </div>
  );
};

export default FormattedMessage;
